/**
 * Special license input field for a vehicle register numbers
 */
class LicenseInput {

  /**
   * @constructor
   * @param {HTMLInputElement} element element to bind license input logic
   * @throws {TypeError} throws an error if element parameter is not instance of HTMLInputElement
   */
  constructor( element ) {

    if( !(element instanceof HTMLInputElement) ) {
      throw new TypeError('Given element is not an HTMLInputElement');
    }

    this._element = element;
    this._container = element.closest('.form-v10__field');

    this._license_1_name = `${this._element.name}_1`;
    this._license_2_name = `${this._element.name}_2`;

    //These are actual input elements shown in the UI
    this._partials = {
      license_1: this._createPartialElement(this._license_1_name),
      license_2: this._createPartialElement(this._license_2_name)
    };

    // Set values
    if (this._element.value) {
      this._partials.license_1.value = this._element.value.split('-')[0].slice(0,3);
      this._partials.license_2.value = this._element.value.split('-')[1].slice(0,3);
    }

    // Set required attribute
    if (this._element.required) {
      this._partials.license_1.required = true;
      this._partials.license_2.required = true;
    }

    // Set placeholders
    this._partials.license_1.placeholder = 'ABC';
    this._partials.license_2.placeholder = '123';

    // Append elements to DOM
    this._element.parentNode.append(this._partials.license_1);
    this._element.parentNode.append(this._partials.license_2);

    //Hide actual input element
    this._element.type = 'hidden';

    this._bind();
  }

  /**
   * Bind event listeners to license input field
   */
  _bind() {
    this._partials.license_1.addEventListener('input', this._onInput.bind(this) );
    this._partials.license_2.addEventListener('input', this._onInput.bind(this) );
    this._partials.license_1.addEventListener('keydown', this._onKeyDown.bind(this) );
    this._partials.license_2.addEventListener('keydown', this._onKeyDown.bind(this) );
    this._partials.license_1.addEventListener('paste', this._onPaste.bind(this) );
    this._partials.license_2.addEventListener('paste', this._onPaste.bind(this) );
    this._partials.license_1.addEventListener('copy', this._onCopy.bind(this) );
    this._partials.license_2.addEventListener('copy', this._onCopy.bind(this) );
  }

  /**
   * Called when a input element value is changed
   * @param {Event} event event object
   */
  _onInput( event ) {

    let selectionStart = event.target.selectionStart;
    let selectionEnd = event.target.selectionEnd;

    const regex = /[^\w\d]/g; // Check if contains other than alphanumeric characters

    // Remove non-alphanumeric characters
    if (event.target.value && regex.test(event.target.value)) {
      event.target.value = event.target.value.replace(regex, '');
    }

    // Set values to uppercase
    if (event.target.value.toUpperCase() !== event.target.value) {
      event.target.value = event.target.value.toUpperCase();
    }

    if (event.target.id === this._license_1_name && event.target.value.length === 3 && selectionStart === 3) {
      selectionEnd = this._partials.license_2.value.length;

      this._partials.license_2.focus();
      this._partials.license_2.setSelectionRange(0, selectionEnd);
    } else {
      event.target.setSelectionRange(selectionStart, selectionEnd);
    }

    this._element.value = this.getValue();
  }

  /**
   * Called when a button is pressed
   * @param {Event} event event object
   */
  _onKeyDown(event) {

    const KEYCODE_ENTER = 13;
    const KEYCODE_BACKSPACE = 8;
    const KEYCODE_LEFT_ARROW = 37;
    const KEYCODE_RIGHT_ARROW = 39;

    const keyCode = event.keyCode || event.charCode;

    let focusedOut = false;
    let selectionStart = event.target.selectionStart;
    let selectionEnd = event.target.selectionEnd;

    // Enter pressed
    if (keyCode === KEYCODE_ENTER) {
      event.preventDefault();
    }

    if (event.target.id === this._license_1_name) {

      // Right arrow key or enter pressed
      if ( (selectionStart === event.target.value.length && keyCode === KEYCODE_RIGHT_ARROW) || keyCode === KEYCODE_ENTER ) {
        selectionStart = 0;
        selectionEnd = selectionStart;

        // Enter key pressed
        if (keyCode === KEYCODE_ENTER) {
          selectionEnd = this._partials.license_2.value.length;
        }

        this._partials.license_2.focus(); // Focus on the second input field
        this._partials.license_2.setSelectionRange(selectionStart, selectionEnd); // Set cursor position

        focusedOut = true;
      }

    } else if (event.target.id === this._license_2_name) {

      // Left arrow key or backspace pressed
      if ( selectionStart === 0 && (keyCode === KEYCODE_LEFT_ARROW || (keyCode === KEYCODE_BACKSPACE && selectionStart === selectionEnd)) ) {
        selectionStart = this._partials.license_1.value.length;
        selectionEnd = selectionStart;

        this._partials.license_1.focus(); // Focus on the first input field
        this._partials.license_1.setSelectionRange(selectionStart, selectionEnd); // Set cursor position

        focusedOut = true;
      }
    }

    // This prevents cursor skipping when focusing to another input field
    if (focusedOut) {
      event.preventDefault();
      focusedOut = false;
    }

    this._handleValidation()
  }

  /**
   * Handle simple validation if field is marked as required.
   */
  _handleValidation() {
    if( this._container.classList.contains('stage-required') ) {
      const value = this.getValue();
      this._container.classList.toggle('stage-error', value.length === 0);
    }
  }

  /**
   * Called when data is copied from the input field
   * @param {Event} event event object
   */
  _onCopy(event) {
    event.clipboardData.setData('text/plain', this.getValue());
    event.preventDefault();
  }

  /**
   * Return license as a string, returned value sould be something like 'ABC-123'
   * @returns {string} license as a string
   */
  getValue() {
    const value = this._partials.license_1.value || this._partials.license_2.value ? `${this._partials.license_1.value}-${this._partials.license_2.value}` : '';
    return value;
  }

  /**
   * Called when a information is pasted to input field
   * @param {Event} event event object
   */
  _onPaste(event) {
    event.preventDefault();

    let paste = (event.clipboardData || window.clipboardData).getData('text');
    // eslint-disable-next-line no-useless-escape
    paste = paste.replace(/[^\w\d\-]/gi, '').toUpperCase();

    let pasteArr = [];

    if (paste.indexOf('-') === -1) {
      const paste_1 = paste.slice(0,3);
      const paste_2 = paste.slice(3,6);

      if (paste_1) {
        pasteArr.push(paste_1);
      }

      if (paste_2) {
        pasteArr.push(paste_2);
      }

    } else {
      pasteArr = paste.split('-');
    }

    if (pasteArr[0]) {
      // eslint-disable-next-line no-useless-escape
      this._partials.license_1.value = pasteArr[0].slice(0,3).replace(/\-/g, '');
    }

    if (pasteArr[1]) {
      // eslint-disable-next-line no-useless-escape
      this._partials.license_2.value = pasteArr[1].slice(0,3).replace(/\-/g, '');
    }

    if (pasteArr.length) {
      this._partials.license_2.focus();
      this._partials.license_2.setSelectionRange(this._partials.license_2.value.length,
        this._partials.license_2.value.length);
    }
  }

  /**
   * Create partial input element object
   * @param {string} id id and name attribute value
   * @returns {HTMLInputElement} input element
   */
  _createPartialElement( id ) {
    const inputElement = document.createElement('input');

    inputElement.name = id;
    inputElement.id = id;
    inputElement.type = 'text';
    inputElement.setAttribute('maxlength', 3);

    return inputElement;
  }

}

export default LicenseInput;
